import classNames from 'classnames'
import React, { ReactNode, useState } from 'react'
import Link from 'next/link'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'

import { formattedStats } from 'constants/marketing-stats'
import Icon from 'components/Icon'
import ButtonAnchor from 'components/ButtonAnchor'
import ButtonLink from 'components/ButtonLink'
import Logo from 'components/Logo'
import News from './Icon/News'
import Stock from './Icon/Stock'
import Arrow from './Icon/Arrow'
import Gifts from './Icon/Gifts'
import Games from './Icon/Games'
import Online from './Icon/Online'
import Connect from './Icon/Connect'
import Fashion from './Icon/Fashion'
import Schools from './Icon/Schools'
import PetShops from './Icon/PetShops'
import Florists from './Icon/Florists'
import Pharmacy from './Icon/Pharmacy'
import BookShops from './Icon/BookShops'
import CreateAccount from './Icon/CreateAccount'
import SmallBusiness from './Icon/SmallBusiness'
import styles from './MarketingRetailerPageContent.module.css'

const MarketingRetailerPageContent = () => {
  const cx = classNames.bind(styles)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [swiperRef, setSwiperRef] = useState<any>()
  const testimonialCarousalData: Array<{ image: string; description: string; author: ReactNode }> = [
    {
      image: 'testimonial-1.jpg',
      description:
        '“Fieldfolio is great because there are so many different wholesalers and products available in one place. It makes ordering simple while trying to run a busy business!',
      author: (
        <>
          Geoff & Karen, <strong>Stamp & Grind</strong>
        </>
      )
    },
    {
      image: 'testimonial-2.jpg',
      description:
        "“Fieldfolio is so easy to use and a great time saver. I've discovered new products and wholesalers without having to go hunting. I only wish more wholesalers used it!”",
      author: (
        <>
          Esther Beecroft, <strong>Royal Tasmanian Botanical Gardens</strong>
        </>
      )
    }
  ]

  return (
    <div className={styles.root}>
      <section className={styles.heroSection}>
        <div className={styles.hero}>
          <div className={styles.logoContainer}>
            <Logo isReversed />
          </div>
          <div className={styles.heroContent}>
            <h1 className={styles.heroTitle}>Your one-stop shop for wholesale</h1>
            <p className={styles.heroSubheadLine}>
              Shop over {formattedStats.products} products from thousands of wholesalers, all in one place.
            </p>
            <div className={styles.buttonContainer}>
              <Link href="/signup/buyer" passHref>
                <ButtonAnchor kind="primary" className={styles.button} labelStyle={styles.buttonLabel}>
                  Signup to Buy
                </ButtonAnchor>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.statsSection}>
        <div className={styles.statsSectionContent}>
          <div className={styles.statsItem}>
            <h1 className={styles.statsItemTitle}>{formattedStats.products}+</h1>
            <h3 className={styles.statsItemSubtitle}>Wholesale Products</h3>
            <p className={styles.statsItemText}>
              With one of the largest wholesale product ranges anywhere, nothing is too hard to find
            </p>
          </div>
          <div className={styles.statsItem}>
            <h1 className={styles.statsItemTitle}>{formattedStats.sellers}+</h1>
            <h3 className={styles.statsItemSubtitle}>Wholesalers</h3>
            <p className={styles.statsItemText}>Thousands of leading wholesalers do business on Fieldfolio every day</p>
          </div>
          <div className={styles.statsItem}>
            <h1 className={styles.statsItemTitle}>{formattedStats.buyers}+</h1>
            <h3 className={styles.statsItemSubtitle}>Buyers</h3>
            <p className={styles.statsItemText}>
              Over {formattedStats.buyers} retailers and other buyers have already chosen Fieldfolio for their wholsale
              purchasing
            </p>
          </div>
          <div className={styles.statsItem}>
            <h1 className={styles.statsItemTitle}>$165 million+</h1>
            <h3 className={styles.statsItemSubtitle}>Annually</h3>
            <p className={styles.statsItemText}>Over $165 million in orders annually, and growing fast!</p>
          </div>
        </div>
      </section>

      <section className={styles.productsSection}>
        <div className={styles.sectionInfo}>
          <h2 className={styles.sectionTitle}>Unique wholesale products, curated for you</h2>
          <p className={styles.sectionDescription}>
            Get the latest on new and updated products, catalogs and suppliers.
          </p>
        </div>
        <div className={styles.productItemsContainer}>
          <Link href="/shop/d/home" passHref>
            <a target="_blank" className={styles.productItem}>
              <img className={styles.productItemImage} src="/marketing/retailer-page/home.jpg" alt="Home Image" />
              <div className={styles.productItemInfo}>
                <h3 className={styles.productItemTitle}>Home</h3>
                <p className={styles.productItemDescription}>Art, Candles, Collectables and more...</p>
              </div>
            </a>
          </Link>
          <Link href="/shop/d/fashion">
            <a target="_blank" className={styles.productItem}>
              <img className={styles.productItemImage} src="/marketing/retailer-page/fashion.jpg" alt="Fashion Image" />
              <div className={styles.productItemInfo}>
                <h3 className={styles.productItemTitle}>Fashion</h3>
                <p className={styles.productItemDescription}>Accessories, Bags, Clothing and more...</p>
              </div>
            </a>
          </Link>
          <Link href="/shop/d/kitchen">
            <a target="_blank" className={styles.productItem}>
              <img className={styles.productItemImage} src="/marketing/retailer-page/kitchen.jpg" alt="Kitchen Image" />
              <div className={styles.productItemInfo}>
                <h3 className={styles.productItemTitle}>Kitchen</h3>
                <p className={styles.productItemDescription}>Gadgets, Glassware, utensils and more...</p>
              </div>
            </a>
          </Link>
        </div>
      </section>

      <section className={styles.purchaseSection}>
        <div className={styles.purchaseContent}>
          <div className={styles.sectionInfo}>
            <h2 className={styles.sectionTitle}>Wholesale purchasing has never been so easy</h2>
            <p className={styles.sectionDescription}>
              Fieldfolio is like a tradeshow that never closes. <br className={styles.lineBreak} />
              Browse and order new stock anywhere, anytime from thousands of wholesalers across Australia and New
              Zealand.
            </p>
          </div>
          <div className={styles.purchaseStepsContainer}>
            <div className={styles.purchaseStep}>
              <div className={styles.purchaseIcon}>
                <CreateAccount />
              </div>
              <h2 className={styles.purchaseStepDesc}>
                Create a free
                <br />
                retailer account
              </h2>
            </div>
            <div className={styles.purchaseArrowIcon}>
              <Arrow />
            </div>
            <div className={styles.purchaseStep}>
              <div className={styles.purchaseIcon}>
                <Connect />
              </div>
              <h2 className={styles.purchaseStepDesc}>
                Connect with <br className={styles.lineBreak} />
                over one thousand wholesalers
              </h2>
            </div>
            <div className={styles.purchaseArrowIcon}>
              <Arrow />
            </div>
            <div className={styles.purchaseStep}>
              <div className={styles.purchaseIcon}>
                <Stock />
              </div>
              <h2 className={styles.purchaseStepDesc}>Purchase goods wholesale</h2>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.testimonialCarousalSection}>
        <Swiper
          onSwiper={swiper => setSwiperRef(swiper)}
          slidesPerView={1}
          loop={false}
          loopedSlides={testimonialCarousalData.length}
          freeMode>
          {testimonialCarousalData.map((slideData, index) => (
            <SwiperSlide key={index}>
              <div className={styles.testimonialContainer}>
                <img
                  className={styles.testimonialCarousalImage}
                  src={`/marketing/retailer-page/${slideData.image}`}
                  alt="Testimonial Image"
                />
                <div className={styles.testimonialContent}>
                  <h1 className={styles.sectionTitle}>{slideData.description}</h1>
                  <p className={styles.sectionDescription}>{slideData.author}</p>
                  <div className={styles.arrowsContainer}>
                    <button
                      className={cx(styles.previousArrow, index === 0 ? styles.disabled : '')}
                      onClick={() => swiperRef.slideTo(index - 1, 500)}
                      disabled={index === 0}>
                      <Icon kind={'button-next'} size={16} color={index === 0 ? '#D4D4D4' : '#3F3F3F'} />
                    </button>
                    <button
                      className={cx(
                        styles.nextArrow,
                        index === testimonialCarousalData.length - 1 ? styles.disabled : ''
                      )}
                      onClick={() => swiperRef.slideTo(index + 1, 500)}
                      disabled={index === testimonialCarousalData.length - 1}>
                      <Icon
                        kind={'button-next'}
                        size={16}
                        color={index === testimonialCarousalData.length - 1 ? '#D4D4D4' : '#3F3F3F'}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </section>

      <section className={styles.mapSection}>
        <div className={styles.mapContentContainer}>
          <img className={styles.mapImage} src="/marketing/marketing-panels/map.jpg" alt="Map Image" />
          <div className={styles.sectionInfo}>
            <h2 className={styles.sectionTitle}>
              Join {formattedStats.buyers}+ retailers across <br className={styles.mapSectionTitleLineBreak} />
              Australia & New Zealand
            </h2>
            <p className={styles.mapSectionDescription}>
              Fieldfolio is the No. 1 wholesale sales platform, with tens of thousands of retailers already on board.{' '}
              <br className={styles.mapSectionLineBreak} />
              Don&apos;t miss out on Australia and New Zealand&apos;s most exciting array of wholesale products!
            </p>
          </div>
          <div className={styles.mapImagesContainer}>
            <div className={styles.mapProduct}>
              <Pharmacy />
              <p className={styles.mapProductsDescription}>Pharmacies</p>
            </div>
            <div className={styles.mapProduct}>
              <News />
              <p className={styles.mapProductsDescription}>Newsagents</p>
            </div>
            <div className={styles.mapProduct}>
              <Gifts />
              <p className={styles.mapProductsDescription}>Gifts & Homewares</p>
            </div>
            <div className={styles.mapProduct}>
              <Fashion />
              <p className={styles.mapProductsDescription}>Fashion Boutiques</p>
            </div>
            <div className={styles.mapProduct}>
              <Games />
              <p className={styles.mapProductsDescription}>Games & Toys</p>
            </div>
            <div className={styles.mapProduct}>
              <Online />
              <p className={styles.mapProductsDescription}>Online Retailers</p>
            </div>
            <div className={styles.mapProduct}>
              <Florists />
              <p className={styles.mapProductsDescription}>Florists</p>
            </div>
            <div className={styles.mapProduct}>
              <BookShops />
              <p className={styles.mapProductsDescription}>Bookshops</p>
            </div>
            <div className={styles.mapProduct}>
              <Schools />
              <p className={styles.mapProductsDescription}>Schools & Community Centres</p>
            </div>
            <div className={styles.mapProduct}>
              <PetShops />
              <p className={styles.mapProductsDescription}>Pet Shops</p>
            </div>
            <div className={styles.mapProduct}>
              <SmallBusiness />
              <p className={styles.mapProductsDescription}>Small Businesses</p>
            </div>
          </div>
        </div>
      </section>

      <section className={styles.buyersItemsSection}>
        <div className={styles.buyersItemsContainer}>
          <Link href="/shop/d/gifts">
            <a target="_blank" className={styles.productItem}>
              <img className={styles.productItemImage} src="/marketing/retailer-page/gifts.jpg" alt="Gifts Image" />
              <div className={styles.productItemInfo}>
                <h3 className={styles.productItemTitle}>Gifts</h3>
                <p className={styles.productItemDescription}>Candles, Fragrances, Soaps and more...</p>
              </div>
            </a>
          </Link>
          <Link href="/shop/d/kids">
            <a target="_blank" className={styles.productItem}>
              <img className={styles.productItemImage} src="/marketing/retailer-page/kids.jpg" alt="Kids Image" />
              <div className={styles.productItemInfo}>
                <h3 className={styles.productItemTitle}>Kids</h3>
                <p className={styles.productItemDescription}>Baby, Games, Toys and more...</p>
              </div>
            </a>
          </Link>
          <Link href="/shop/d/outdoors">
            <a target="_blank" className={styles.productItem}>
              <img className={styles.productItemImage} src="/marketing/retailer-page/outdoor.jpg" alt="Outdoor Image" />
              <div className={styles.productItemInfo}>
                <h3 className={styles.productItemTitle}>Outdoor</h3>
                <p className={styles.productItemDescription}>Adventure, Garden, Travel and more...</p>
              </div>
            </a>
          </Link>
        </div>
      </section>

      <section className={styles.signupSection}>
        <div className={styles.signupContainer}>
          <div className={styles.signupSectionInfo}>
            <h1 className={styles.signupSectionTitle}>Ready to start ordering wholesale online?</h1>
            <div className={styles.signupSectionText}>Get products that your customers will love on Fieldfolio.</div>
            <ButtonLink href="/signup/buyer" className={styles.signupButtonFooter} label="Signup to Buy" />
          </div>
          <img
            className={styles.signupSectionImage}
            src="/marketing/retailer-page/signup.jpg"
            alt="Signup Section Image"
          />
        </div>
      </section>
    </div>
  )
}

export default MarketingRetailerPageContent
